import React, { useState, useEffect } from 'react';
import { Gavel, Play, Pause, SkipForward, DollarSign, Users, Timer, X, CheckCircle, XCircle, Settings, Save, Shuffle, UserPlus, ShoppingCart, MonitorPlay } from 'lucide-react';
import type { Player, Team, AppUser, AuctionSettings } from '../types';
import { useAuction } from '../hooks/useAuction';
import { formatCurrency, toProperCase } from '../utils/helpers';
import PhotoLightbox from './PhotoLightbox';
import PlayerRandomizer from './PlayerRandomizer';

interface AuctionModeProps {
  players: Player[];
  teams: Team[];
  currentUser: AppUser;
  auctionSettings: AuctionSettings | null;
  onSellPlayer: (player: Player, teamId: string, price: number) => Promise<void>;
  onClose: () => void;
}

const AuctionMode: React.FC<AuctionModeProps> = ({ players, teams, currentUser, auctionSettings, onSellPlayer, onClose }) => {
  // Initialize from auctionSettings (from Settings page) or use defaults
  const [basePrice, setBasePrice] = useState(auctionSettings?.minBidPoints || 100);
  const [bidIncrement, setBidIncrement] = useState(auctionSettings?.bidIncrement || 50);
  const [timerDuration, setTimerDuration] = useState(auctionSettings?.timerDuration || 30);
  const [showSettings, setShowSettings] = useState(false);
  const [settingsChanged, setSettingsChanged] = useState(false);
  const [queueFilter, setQueueFilter] = useState({
    gender: 'all' as 'all' | 'male' | 'female',
    side: 'all' as 'all' | 'right' | 'left' | 'both',
    search: ''
  });
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState<{ url: string; name: string }>({ url: '', name: '' });
  const [isRandomizerOpen, setIsRandomizerOpen] = useState(false);

  // Proxy Bidding State
  const [proxyTeamId, setProxyTeamId] = useState<string>('');
  const [proxyCustomBid, setProxyCustomBid] = useState<string>('');

  // Manual Sell Modal State
  const [manualSellOpen, setManualSellOpen] = useState(false);
  const [manualSellTeamId, setManualSellTeamId] = useState('');
  const [manualSellPrice, setManualSellPrice] = useState('');

  const publicDisplayUrl = `${window.location.origin}?display=true`;

  const {
    auction,
    firestoreState,
    isLoading,
    startAuction,
    endAuction,
    updateSettings,
    setShowCurrentBid,
    selectPlayer,
    startBidding,
    pauseBidding,
    resumeBidding,
    skipPlayer,
    finalizeSale,
    manualSell,
    markUnsold,
    adminPlaceBid
  } = useAuction({ players, teams, currentUser, onPlayerSold: onSellPlayer });

  // Initialize from auctionSettings when component loads (before auction starts)
  useEffect(() => {
    if (!firestoreState?.isActive && auctionSettings) {
      setBasePrice(auctionSettings.minBidPoints);
      setBidIncrement(auctionSettings.bidIncrement);
      setTimerDuration(auctionSettings.timerDuration);
    }
  }, [auctionSettings, firestoreState?.isActive]);

  // Sync local state with Firestore state when auction is active
  useEffect(() => {
    if (firestoreState?.isActive) {
      setBasePrice(firestoreState.basePrice);
      setBidIncrement(firestoreState.bidIncrement);
      setTimerDuration(firestoreState.timerDuration);
      setSettingsChanged(false);
    }
  }, [firestoreState?.isActive, firestoreState?.basePrice, firestoreState?.bidIncrement, firestoreState?.timerDuration]);

  // Track if settings changed from Firestore values
  const handleBasePriceChange = (val: number) => {
    setBasePrice(val);
    if (auction.isActive) setSettingsChanged(val !== firestoreState?.basePrice || bidIncrement !== firestoreState?.bidIncrement || timerDuration !== firestoreState?.timerDuration);
  };

  const handleBidIncrementChange = (val: number) => {
    setBidIncrement(val);
    if (auction.isActive) setSettingsChanged(basePrice !== firestoreState?.basePrice || val !== firestoreState?.bidIncrement || timerDuration !== firestoreState?.timerDuration);
  };

  const handleTimerChange = (val: number) => {
    setTimerDuration(val);
    if (auction.isActive) setSettingsChanged(basePrice !== firestoreState?.basePrice || bidIncrement !== firestoreState?.bidIncrement || val !== firestoreState?.timerDuration);
  };

  const getReserveRequirement = (team: Team, includeCurrentPlayer = true) => {
    const totalRequiredPlayers = (auctionSettings?.requiredMalePlayers || 0) + (auctionSettings?.requiredFemalePlayers || 0);
    if (totalRequiredPlayers <= 0) return 0;

    const ownedCount = players.filter(p => p.teamId === team.id && p.status === 'sold').length;
    const projectedOwned = includeCurrentPlayer && ownedCount < totalRequiredPlayers ? ownedCount + 1 : ownedCount;
    const remainingSlots = Math.max(totalRequiredPlayers - projectedOwned, 0);
    const base = auction.basePrice || auctionSettings?.minBidPoints || 0;
    return remainingSlots * base;
  };

  // Save settings to Firestore
  const handleSaveSettings = async () => {
    await updateSettings({ basePrice, bidIncrement, timerDuration });
    setSettingsChanged(false);
    setShowSettings(false);
  };

  const filteredQueue = auction.playerQueue.filter(p => {
    if (queueFilter.gender !== 'all') {
      const g = p.details?.player_gender?.toLowerCase();
      if (g !== queueFilter.gender) return false;
    }
    if (queueFilter.side !== 'all') {
      const s = p.details?.playing_side?.toLowerCase();
      if (s !== queueFilter.side) return false;
    }
    if (queueFilter.search.trim()) {
      const q = queueFilter.search.toLowerCase();
      if (
        !(
          p.name.toLowerCase().includes(q) ||
          p.mobile_number?.includes(q) ||
          p.details?.jersey_name?.toLowerCase().includes(q)
        )
      ) return false;
    }
    return true;
  });

  // Handle starting the auction session
  const handleStartAuction = async () => {
    await startAuction({ basePrice, bidIncrement, timerDuration });
  };

  // Handle exiting - end auction if active
  const handleExit = async () => {
    if (auction.isActive) {
      await endAuction();
    }
    onClose();
  };

  // Admin Proxy Bid Handler
  const handleProxyBid = async (type: 'next' | 'custom') => {
    if (!proxyTeamId) return;

    if (type === 'custom') {
      const amount = parseInt(proxyCustomBid);
      if (isNaN(amount)) return;
      await adminPlaceBid(proxyTeamId, amount);
      setProxyCustomBid('');
    } else {
      await adminPlaceBid(proxyTeamId);
    }
  };

  const handleQuickProxyBid = async (teamId: string, amount?: number) => {
    if (auction.status !== 'bidding') return;
    await adminPlaceBid(teamId, amount);
  };

  const handleOpenPublicDisplay = () => {
    window.open(publicDisplayUrl, '_blank', 'noopener,noreferrer');
  };

  const handleToggleBidVisibility = () => {
    setShowCurrentBid(!auction.showCurrentBid);
  };

  // Manual Sell Handler
  const handleManualSell = async () => {
    if (!auction.currentPlayer || !manualSellTeamId || !manualSellPrice) return;

    const price = parseInt(manualSellPrice);
    if (isNaN(price)) return;

    await manualSell(auction.currentPlayer, manualSellTeamId, price);
    setManualSellOpen(false);
    setManualSellTeamId('');
    setManualSellPrice('');
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-50 bg-gradient-to-br from-indigo-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Gavel className="w-16 h-16 text-yellow-400 animate-bounce mx-auto mb-4" />
          <p className="text-white text-xl">Loading Auction...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-gradient-to-br from-indigo-900 via-purple-900 to-slate-900 overflow-auto">
      {/* Header */}
      <div className="bg-black/30 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Gavel className="w-8 h-8 text-yellow-400" />
            <h1 className="text-2xl font-bold text-white">Live Auction - Admin Control</h1>
            {auction.isActive && (
              <span className="px-2 py-1 bg-green-500 text-white text-xs rounded-full animate-pulse">LIVE</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleOpenPublicDisplay}
              className="px-4 py-2 rounded-lg bg-gradient-to-r from-indigo-500 to-blue-500 text-white shadow-lg shadow-indigo-500/20 hover:from-indigo-600 hover:to-blue-600 transition transform hover:-translate-y-0.5 flex items-center gap-2"
            >
              <MonitorPlay className="w-4 h-4" />
              Public Display
            </button>
            <button
              onClick={handleToggleBidVisibility}
              className={`px-4 py-2 rounded-lg border flex items-center gap-2 text-sm font-semibold transition-colors ${
                auction.showCurrentBid
                  ? 'border-green-400/40 text-green-100 bg-green-500/10 hover:bg-green-500/20'
                  : 'border-yellow-400/40 text-yellow-100 bg-yellow-500/10 hover:bg-yellow-500/20'
              }`}
            >
              {auction.showCurrentBid ? 'Hide Current Bid' : 'Show Current Bid'}
            </button>
            {auction.isActive && (
              <button
                onClick={() => setShowSettings(!showSettings)}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                  showSettings ? 'bg-indigo-600 text-white' : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                <Settings className="w-4 h-4" /> Settings
              </button>
            )}
            <button onClick={handleExit} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2">
              <X className="w-4 h-4" /> Exit Auction
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Settings Panel (when not active - Start New Session) */}
        {!auction.isActive && (
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 mb-6">
            <h2 className="text-lg font-semibold text-white mb-4">Start New Auction Session</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm text-white/70 mb-1">Base Price (pts)</label>
                <input type="number" value={basePrice} onChange={e => setBasePrice(Number(e.target.value))}
                  className="w-full px-3 py-2 rounded-lg bg-white/20 text-white border border-white/30" />
              </div>
              <div>
                <label className="block text-sm text-white/70 mb-1">Bid Increment (pts)</label>
                <input type="number" value={bidIncrement} onChange={e => setBidIncrement(Number(e.target.value))}
                  className="w-full px-3 py-2 rounded-lg bg-white/20 text-white border border-white/30" />
              </div>
              <div>
                <label className="block text-sm text-white/70 mb-1">Timer (seconds)</label>
                <input type="number" value={timerDuration} onChange={e => setTimerDuration(Number(e.target.value))}
                  className="w-full px-3 py-2 rounded-lg bg-white/20 text-white border border-white/30" />
              </div>
            </div>
            <button onClick={handleStartAuction}
              className="w-full py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold flex items-center justify-center gap-2">
              <Play className="w-5 h-5" /> Start Auction Session
            </button>
            <p className="text-white/50 text-sm mt-2 text-center">
              Team owners can join from their devices once the session starts
            </p>
          </div>
        )}

        {/* Settings Panel (when active - Edit Settings) */}
        {auction.isActive && showSettings && (
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <Settings className="w-5 h-5" /> Auction Settings
              </h2>
              <div className="flex items-center gap-2">
                <span className="text-xs text-white/50 bg-white/10 px-2 py-1 rounded">
                  Base: {firestoreState?.basePrice} | Inc: {firestoreState?.bidIncrement} | Timer: {firestoreState?.timerDuration}s
                </span>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm text-white/70 mb-1">Base Price (pts)</label>
                <input
                  type="number"
                  value={basePrice}
                  onChange={e => handleBasePriceChange(Number(e.target.value))}
                  className="w-full px-3 py-2 rounded-lg bg-white/20 text-white border border-white/30 focus:border-indigo-400 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-white/70 mb-1">Bid Increment (pts)</label>
                <input
                  type="number"
                  value={bidIncrement}
                  onChange={e => handleBidIncrementChange(Number(e.target.value))}
                  className="w-full px-3 py-2 rounded-lg bg-white/20 text-white border border-white/30 focus:border-indigo-400 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-white/70 mb-1">Timer (seconds)</label>
                <input
                  type="number"
                  value={timerDuration}
                  onChange={e => handleTimerChange(Number(e.target.value))}
                  className="w-full px-3 py-2 rounded-lg bg-white/20 text-white border border-white/30 focus:border-indigo-400 focus:outline-none"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleSaveSettings}
                disabled={!settingsChanged}
                className={`flex-1 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors ${
                  settingsChanged
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-white/10 text-white/50 cursor-not-allowed'
                }`}
              >
                <Save className="w-5 h-5" /> Save Settings
              </button>
              <button
                onClick={() => {
                  setShowSettings(false);
                  // Reset to Firestore values
                  if (firestoreState) {
                    setBasePrice(firestoreState.basePrice);
                    setBidIncrement(firestoreState.bidIncrement);
                    setTimerDuration(firestoreState.timerDuration);
                  }
                  setSettingsChanged(false);
                }}
                className="px-6 py-3 bg-white/10 text-white rounded-lg hover:bg-white/20"
              >
                Cancel
              </button>
            </div>
            <p className="text-white/50 text-sm mt-2 text-center">
              Changes will apply to the next player. Current bidding will continue with current settings.
            </p>
          </div>
        )}

        {/* Main Auction Area (when active) */}
        {auction.isActive && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left: Player Queue with filters */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 space-y-3">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Users className="w-5 h-5" /> Player Queue ({filteredQueue.length}/{auction.playerQueue.length})
              </h3>

              {/* Filters */}
              <div className="space-y-3">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <input
                      type="text"
                      value={queueFilter.search}
                      onChange={e => setQueueFilter(prev => ({ ...prev, search: e.target.value }))}
                      placeholder="Search..."
                      className="w-full px-3 py-2 rounded-lg bg-white/15 border border-white/20 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    />
                  </div>
                  <button
                    onClick={() => setIsRandomizerOpen(true)}
                    disabled={filteredQueue.length === 0 || auction.status !== 'idle'}
                    className={`px-3 py-2 rounded-lg flex items-center justify-center gap-2 font-semibold transition-colors ${
                      filteredQueue.length > 0 && auction.status === 'idle'
                        ? 'bg-gradient-to-r from-pink-500 to-rose-500 text-white hover:from-pink-600 hover:to-rose-600'
                        : 'bg-white/10 text-white/40 cursor-not-allowed'
                    }`}
                    title={auction.status !== 'idle' ? "Cannot pick during auction" : "Pick Random Player"}
                  >
                    <Shuffle className="w-4 h-4" />
                    <span className="hidden sm:inline">Random</span>
                  </button>
                </div>

                {/* Chip Filters */}
                <div className="flex flex-wrap gap-2">
                  {/* Gender Chips */}
                  <div className="flex bg-white/10 rounded-lg p-1 gap-1">
                    {(['all', 'male', 'female'] as const).map(g => (
                       <button
                         key={g}
                         onClick={() => setQueueFilter(prev => ({ ...prev, gender: g }))}
                         className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                           queueFilter.gender === g
                             ? 'bg-indigo-600 text-white shadow-sm'
                             : 'text-white/70 hover:bg-white/10 hover:text-white'
                         }`}
                       >
                         {g === 'all' ? 'All' : g.charAt(0).toUpperCase() + g.slice(1)}
                       </button>
                    ))}
                  </div>

                  {/* Side Chips */}
                  <div className="flex bg-white/10 rounded-lg p-1 gap-1">
                    {(['all', 'right', 'left', 'both'] as const).map(s => (
                       <button
                         key={s}
                         onClick={() => setQueueFilter(prev => ({ ...prev, side: s }))}
                         className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                           queueFilter.side === s
                             ? 'bg-indigo-600 text-white shadow-sm'
                             : 'text-white/70 hover:bg-white/10 hover:text-white'
                         }`}
                       >
                         {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
                       </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {filteredQueue.map(player => (
                  <div key={player.id}
                    className={`flex items-center justify-between p-3 rounded-lg transition-all ${
                      auction.currentPlayer?.id === player.id
                        ? 'bg-yellow-500/30 ring-2 ring-yellow-400'
                        : 'bg-white/5 hover:bg-white/10'
                    }`}>
                    <div className="flex items-center gap-2">
                      {player.details?.photo_url && (
                        <img
                          src={player.details.photo_url}
                          alt=""
                          className="w-8 h-8 rounded-full cursor-pointer"
                          onClick={() => {
                            setLightboxImage({ url: player.details?.photo_url || '', name: player.name });
                            setLightboxOpen(true);
                          }}
                        />
                      )}
                      <div>
                        <span className="text-white block">{toProperCase(player.name)}</span>
                        <span className="text-xs text-white/60">{player.mobile_number}</span>
                      </div>
                    </div>
                    {auction.status === 'idle' && auction.currentPlayer?.id !== player.id && (
                      <button onClick={() => selectPlayer(player)}
                        className="px-3 py-1 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700">
                        Select
                      </button>
                    )}
                  </div>
                ))}
                {filteredQueue.length === 0 && (
                  <p className="text-white/50 text-center py-4">No players match filters</p>
                )}
              </div>
            </div>

          {/* Center: Current Auction */}
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 relative">
            {auction.currentPlayer ? (
              <div className="text-center">
                {/* Status Badge */}
                {auction.status === 'sold' && (
                  <div className="mb-4 py-2 px-4 bg-green-500 text-white rounded-lg inline-flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" /> SOLD!
                  </div>
                )}
                {auction.status === 'unsold' && (
                  <div className="mb-4 py-2 px-4 bg-red-500 text-white rounded-lg inline-flex items-center gap-2">
                    <XCircle className="w-5 h-5" /> UNSOLD
                  </div>
                )}

                {/* Timer */}
                {(auction.status === 'bidding' || auction.status === 'paused') && (
                  <div className={`text-6xl font-bold mb-4 ${
                    auction.status === 'paused' ? 'text-orange-400' :
                    auction.timer <= 3 ? 'text-red-400 animate-pulse' : 'text-yellow-400'
                  }`}>
                    <Timer className="w-8 h-8 inline mr-2" />
                    {auction.timer}s
                    {auction.status === 'paused' && <span className="text-lg ml-2">(PAUSED)</span>}
                  </div>
                )}

                {/* Player Info */}
                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-6 mb-4">
                  {auction.currentPlayer.details?.photo_url && (
                    <img src={auction.currentPlayer.details.photo_url} alt=""
                      className="w-24 h-24 rounded-full mx-auto mb-3 border-4 border-white/30" />
                  )}
                  <h2 className="text-2xl font-bold text-white">{toProperCase(auction.currentPlayer.name)}</h2>
                  <p className="text-white/70">{auction.currentPlayer.details?.playing_side || 'Unknown'} Side</p>
                </div>

                {/* Current Bid */}
                <div className="bg-green-600/30 rounded-xl p-4 mb-4">
                  <p className="text-white/70 text-sm">Current Bid</p>
                  <p className="text-4xl font-bold text-green-400">{formatCurrency(auction.currentBid)} pts</p>
                  {auction.currentBidder && (
                    <p className="text-white mt-2">by <span className="font-semibold">{auction.currentBidder.name}</span></p>
                  )}
                </div>

                {/* Admin Controls */}
                <div className="space-y-4">
                   <div className="flex flex-wrap gap-2 justify-center">
                     {auction.status === 'idle' && (
                       <button onClick={startBidding}
                         className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2">
                         <Play className="w-4 h-4" /> Start Bidding
                       </button>
                     )}
                     {auction.status === 'bidding' && (
                       <button onClick={pauseBidding}
                         className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 flex items-center gap-2">
                         <Pause className="w-4 h-4" /> Pause
                       </button>
                     )}
                     {auction.status === 'paused' && (
                       <button onClick={resumeBidding}
                         className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2">
                         <Play className="w-4 h-4" /> Resume
                       </button>
                     )}
                     <button onClick={skipPlayer}
                       className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center gap-2">
                       <SkipForward className="w-4 h-4" /> Skip
                     </button>
                     {auction.currentBidder && auction.status !== 'sold' && (
                       <button onClick={finalizeSale}
                         className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2">
                         <Gavel className="w-4 h-4" /> Sell Now
                       </button>
                     )}
                     {!auction.currentBidder && auction.status !== 'unsold' && (
                       <>
                         <button onClick={markUnsold}
                           className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2">
                           <XCircle className="w-4 h-4" /> Mark Unsold
                         </button>
                         <button onClick={() => setManualSellOpen(true)}
                           className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2"
                           title="Offline / Manual Sale"
                         >
                           <ShoppingCart className="w-4 h-4" /> Offline Sale
                         </button>
                       </>
                     )}
                   </div>

                   {/* Proxy Bidding Panel */}
                   {auction.status === 'bidding' && (
                     <div className="bg-white/10 rounded-lg p-3 mt-4 border border-white/10">
                       <h4 className="text-sm font-semibold text-white/70 mb-2 flex items-center gap-2">
                         <UserPlus className="w-4 h-4" /> Admin Proxy Bid
                       </h4>
                       <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                         <select
                           value={proxyTeamId}
                           onChange={e => setProxyTeamId(e.target.value)}
                           className="w-full bg-black/30 text-white border border-white/20 rounded px-2 py-1 text-sm focus:outline-none focus:border-indigo-500"
                         >
                           <option value="">Select Team...</option>
                           {teams.filter(t => t.id !== auction.currentBidder?.id).map(t => (
                             <option key={t.id} value={t.id}>{t.name} ({formatCurrency(t.currentBalance)}pts)</option>
                           ))}
                         </select>

                         <button
                           onClick={() => handleProxyBid('next')}
                           disabled={!proxyTeamId}
                           className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm py-1 px-2 rounded"
                         >
                          Bid +{formatCurrency(auction.bidIncrement)}
                         </button>
                       </div>
                       <div className="flex gap-2 mt-2">
                          <input
                            type="number"
                            placeholder="Custom Amount"
                            value={proxyCustomBid}
                            onChange={e => setProxyCustomBid(e.target.value)}
                            className="flex-1 bg-black/30 text-white border border-white/20 rounded px-2 py-1 text-sm"
                          />
                          <button
                            onClick={() => handleProxyBid('custom')}
                            disabled={!proxyTeamId || !proxyCustomBid}
                            className="bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm py-1 px-3 rounded"
                          >
                            Bid
                          </button>
                       </div>
                     </div>
                   )}
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <Gavel className="w-16 h-16 text-white/30 mx-auto mb-4" />
                <p className="text-white/50 text-lg">Select a player from the queue</p>
              </div>
            )}
          </div>

          {/* Right: Teams Status Panel */}
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
            <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
              <DollarSign className="w-5 h-5" /> Team Balances
            </h3>
            <div className="space-y-2">
              {teams.map(team => {
                const isCurrentBidder = auction.currentBidder?.id === team.id;
                const baseRequiredBid = auction.currentBidder
                  ? auction.currentBid + auction.bidIncrement
                  : auction.currentBid;
                const reserveNeeded = getReserveRequirement(team);
                const availableForBidding = team.currentBalance - reserveNeeded;
                const canAffordNextBid = availableForBidding >= baseRequiredBid && baseRequiredBid > 0;
                const maxAffordableMultiplier = baseRequiredBid > 0
                  ? Math.max(Math.floor((availableForBidding - baseRequiredBid) / auction.bidIncrement), 0)
                  : 0;
                const quickBidAmount = (multiplier: number) => baseRequiredBid + auction.bidIncrement * multiplier;

                return (
                  <div
                    key={team.id}
                    className={`p-3 rounded-lg transition-all ${
                      isCurrentBidder
                        ? 'bg-green-600/30 ring-2 ring-green-400'
                        : !canAffordNextBid
                          ? 'bg-red-900/20'
                          : 'bg-white/5'
                    }`}
                  >
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <p className="text-white font-medium">{team.name}</p>
                        <p className={`text-sm ${canAffordNextBid ? 'text-white/60' : 'text-red-400'}`}>
                          {formatCurrency(team.currentBalance)} pts
                        </p>
                      </div>
                      <div className="flex flex-col items-start sm:items-end gap-2 w-full sm:w-auto">
                        {auction.status === 'bidding' && (
                            <div className="flex flex-wrap gap-2 justify-end">
                            {[0, 1, 2].map(multiplier => {
                              const amount = quickBidAmount(multiplier);
                              const disabled = !canAffordNextBid || isCurrentBidder || amount > availableForBidding;

                              return (
                                <button
                                  key={multiplier}
                                  onClick={() => handleQuickProxyBid(team.id, amount)}
                                  disabled={disabled}
                                  className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                                    disabled
                                      ? 'bg-white/5 border border-white/10 text-white/30 cursor-not-allowed'
                                      : 'bg-indigo-600/90 border border-indigo-300/50 text-white hover:bg-indigo-700'
                                  }`}
                                >
                                  {multiplier === 0 ? 'Base' : `+${multiplier}x`} ({formatCurrency(amount)})
                                </button>
                              );
                            })}
                            {maxAffordableMultiplier > 0 && (
                              <button
                                onClick={() => handleQuickProxyBid(team.id, quickBidAmount(maxAffordableMultiplier))}
                                disabled={isCurrentBidder}
                                className={`px-3 py-1.5 text-xs font-bold rounded-md transition-colors ${
                                  isCurrentBidder
                                    ? 'bg-white/5 border border-white/10 text-white/30 cursor-not-allowed'
                                    : 'bg-yellow-400/90 border border-yellow-200/60 text-yellow-900 hover:bg-yellow-300'
                                }`}
                              >
                                Max ({formatCurrency(quickBidAmount(maxAffordableMultiplier))})
                              </button>
                            )}
                          </div>
                        )}
                        {isCurrentBidder && (
                          <span className="px-3 py-1 bg-green-600 text-white text-sm rounded-full">
                            Leading
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 pt-4 border-t border-white/10">
              <p className="text-white/50 text-sm text-center">
                Team owners can bid from their devices
              </p>
            </div>
          </div>
        </div>
        )}
      </div>

      <PhotoLightbox
        imageUrl={lightboxImage.url}
        playerName={lightboxImage.name}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />

      {isRandomizerOpen && (
        <PlayerRandomizer
          candidates={filteredQueue}
          onClose={() => setIsRandomizerOpen(false)}
          onComplete={(player) => {
            selectPlayer(player);
            setIsRandomizerOpen(false);
          }}
        />
      )}

      {/* Manual Sell Modal */}
      {manualSellOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-slate-800 rounded-xl max-w-md w-full p-6 shadow-2xl border border-white/10">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-indigo-400" /> Offline / Manual Sale
            </h3>
            <p className="text-white/60 mb-6 text-sm">
              Sell <strong>{auction.currentPlayer?.name}</strong> immediately without bidding.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-white/80 mb-1">Select Team</label>
                <select
                  value={manualSellTeamId}
                  onChange={e => setManualSellTeamId(e.target.value)}
                  className="w-full bg-black/30 text-white border border-white/20 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 outline-none"
                >
                  <option value="">Choose a team...</option>
                  {teams.map(t => (
                    <option key={t.id} value={t.id}>{t.name} (Bal: {formatCurrency(t.currentBalance)})</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-white/80 mb-1">Final Price</label>
                <input
                  type="number"
                  value={manualSellPrice}
                  onChange={e => setManualSellPrice(e.target.value)}
                  placeholder="Enter sold amount"
                  className="w-full bg-black/30 text-white border border-white/20 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 outline-none"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setManualSellOpen(false)}
                className="flex-1 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleManualSell}
                disabled={!manualSellTeamId || !manualSellPrice}
                className="flex-1 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
              >
                Confirm Sale
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuctionMode;
